import Dexie from 'dexie';

const DB_NAME = 'FigshelfFeedDB';
const DB_VERSION = 1;

const normalizeIso = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

export const makeFeedKey = ({ userId, feedType, targetUser, publicFeed }) => {
  const uid = userId || (publicFeed ? 'public' : 'anon');
  const type = feedType || 'following';
  const target = targetUser || '';
  const pub = publicFeed ? '1' : '0';
  return `${uid}:${type}:${target}:${pub}`;
};

class FeedCacheDb extends Dexie {
  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      feedPosts: '[feedKey+postId], feedKey, postId, createdAt',
      feedState: 'feedKey',
    });

    this.feedPosts = this.table('feedPosts');
    this.feedState = this.table('feedState');
  }
}

let dbInstance = null;

const isDatabaseClosedError = (error) => {
  if (!error) return false;
  if (error?.name === 'DatabaseClosedError') return true;
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('database has been closed')) {
    return true;
  }
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('db.open() was cancelled')) {
    return true;
  }
  return false;
};

const resetFeedCacheDb = () => {
  // Do not call db.close() here.
  // Dexie can throw/reject during close/open cancellation races (e.g. after indexedDB.deleteDatabase()),
  // which can surface as unhandled rejections. Dropping the singleton reference is enough; a fresh
  // instance will be created on the next read/write.
  dbInstance = null;
};

export const getFeedCacheDb = () => {
  if (dbInstance && typeof dbInstance.isOpen === 'function' && !dbInstance.isOpen()) {
    resetFeedCacheDb();
  }

  if (!dbInstance) {
    dbInstance = new FeedCacheDb();
  }
  return dbInstance;
};

const getPostId = (p) => p?.id || p?.postId || p?._id;
const getPostCreatedAt = (p) => normalizeIso(p?.createdAt || p?.created_at || p?.timestamp || p?.date);

export const getCachedFeedPosts = async (feedKey, max = 200) => {
  if (!feedKey) return [];

  let rows;
  try {
    const db = getFeedCacheDb();
    rows = await db.feedPosts.where('feedKey').equals(feedKey).toArray();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetFeedCacheDb();
      try {
        const db = getFeedCacheDb();
        rows = await db.feedPosts.where('feedKey').equals(feedKey).toArray();
      } catch {
        return [];
      }
    } else {
      return [];
    }
  }

  rows.sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    if (aTime !== bTime) return bTime - aTime;
    const aId = String(a.postId || '');
    const bId = String(b.postId || '');
    return bId.localeCompare(aId);
  });

  return rows.slice(0, max).map((r) => r.data).filter(Boolean);
};

export const cacheFeedPosts = async (feedKey, posts, max = 200) => {
  if (!feedKey || !Array.isArray(posts)) return;

  const records = posts
    .filter((p) => getPostId(p))
    .map((p) => {
      const postId = getPostId(p);
      return {
        feedKey,
        postId,
        createdAt: getPostCreatedAt(p) || new Date().toISOString(),
        data: p,
      };
    });

  const run = async () => {
    const db = getFeedCacheDb();
    await db.transaction('rw', db.feedPosts, db.feedState, async () => {
      if (records.length > 0) {
        await db.feedPosts.bulkPut(records);
      }

      await db.feedState.put({ feedKey, updatedAt: new Date().toISOString() });

      if (max && max > 0) {
        const rows = await db.feedPosts.where('feedKey').equals(feedKey).toArray();
        if (rows.length > max) {
          rows.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
          const toDelete = rows.slice(0, rows.length - max);
          if (toDelete.length > 0) {
            await db.feedPosts.bulkDelete(toDelete.map((r) => [r.feedKey, r.postId]));
          }
        }
      }
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetFeedCacheDb();
      try {
        await run();
      } catch {
      }
      return;
    }
    return;
  }
};
